// Phase 2.1 Lite — Public Demo Page
// Route: /demo/:slug · ANONYMOUS · no admin chrome
// Fetches /api/public/demo/{slug} and renders with the existing
// TemplateRenderer. Per design decision: no visible "Demo invitation"
// ribbon on the visual.

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { Loader2 } from 'lucide-react';
import TemplateRenderer from '../components/renderer/TemplateRenderer';
import { V2_TEMPLATES, DEFAULT_V2_TEMPLATE_ID } from '../lib/utils/v2Templates';

const API = process.env.REACT_APP_BACKEND_URL;

// Phase 2.1.2 — Sitoa-branded 1200×630 fallback OG image. Hosted on the
// frontend public folder so it sits on whatever origin serves the SPA
// (`/sitoa-og-default.png` resolves on dev, staging, and prod alike).
const DEFAULT_OG_IMAGE_PATH = '/sitoa-og-default.png';
const DEFAULT_OG_DESCRIPTION = 'A digital wedding invitation demo by Sitoa.';

function absoluteImageUrl(raw) {
  const origin = (typeof window !== 'undefined' && window.location?.origin) || '';
  if (!raw) return `${origin}${DEFAULT_OG_IMAGE_PATH}`;
  if (raw.startsWith('http://') || raw.startsWith('https://')) return raw;
  if (raw.startsWith('/')) {
    // Backend-hosted asset (`/uploads/...`) — prefix with API origin.
    return `${API || origin}${raw}`;
  }
  return raw;
}

export default function PublicDemoPage() {
  const { slug } = useParams();
  const [demo, setDemo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    // Anonymous fetch — no Authorization header.
    axios
      .get(`${API}/api/public/demo/${encodeURIComponent(slug)}`)
      .then((r) => {
        if (!cancelled) setDemo(r.data);
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [slug]);

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gray-50"
        data-testid="public-demo-loading"
      >
        <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
      </div>
    );
  }

  if (notFound || !demo) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gray-50"
        data-testid="public-demo-not-found"
      >
        <div className="text-center px-4">
          <h1 className="text-xl font-light text-gray-700 mb-1">Demo not found</h1>
          <p className="text-xs text-gray-500">This demo link is unavailable.</p>
        </div>
      </div>
    );
  }

  const baseId = demo.v2_template_id || DEFAULT_V2_TEMPLATE_ID;
  const config = V2_TEMPLATES[baseId];
  if (!config) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-gray-50"
        data-testid="public-demo-unsupported"
      >
        <div className="text-center px-4">
          <h1 className="text-xl font-light text-gray-700 mb-1">Demo not available</h1>
          <p className="text-xs text-gray-500">This template is no longer supported.</p>
        </div>
      </div>
    );
  }

  // Merge demo_content into settingsOverrides so widgets that read
  // eventDate/venueName/couple/location can show realistic copy.
  const demoContent = demo.demo_content || {};
  const settingsOverrides = {
    ...(demo.v2_settings_overrides || {}),
    eventDate: demoContent.date || '',
    venueName: demoContent.venue || '',
    venueLocation: demoContent.location || '',
    coupleNames: demoContent.couple_names || '',
  };

  // Phase 2.1.1 — Open Graph + Twitter Card meta tags. Crawlers that
  // execute JS (Slackbot in some modes, modern preview generators) will
  // pick these up; crawlers that don't execute JS (WhatsApp, iMessage,
  // Facebookbot, Twitterbot) will need a real SSR shim — tracked as
  // future work. We still emit them via react-helmet-async so the tags
  // are deterministic and discoverable in production SSR later.
  const pageTitle = demo.name ? `${demo.name} · Sitoa` : 'Demo invitation · Sitoa';
  const pageDescription = demo.description || DEFAULT_OG_DESCRIPTION;
  const pageImage = absoluteImageUrl(demo.preview_image);
  const pageUrl = typeof window !== 'undefined'
    ? `${window.location.origin}/demo/${demo.demo_slug || demo.template_id}`
    : '';

  return (
    <div className="min-h-screen" data-testid="public-demo-page">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        {/* Open Graph */}
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={pageImage} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="Sitoa" />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={pageImage} />
      </Helmet>
      <TemplateRenderer
        templateConfig={config}
        invitationData={demo.v2_content || {}}
        themeOverrides={demo.v2_theme_overrides || {}}
        settingsOverrides={settingsOverrides}
        slug={`demo-${demo.demo_slug || demo.template_id}`}
        layoutVersion={demo.layout_version || 'legacy-flat'}
      />
    </div>
  );
}
